import type { FieldPath, FieldValues } from "react-hook-form";

import {
  type BusinessSearchFieldProps,
  BusinessSearchField,
} from "../business-search-field";
import type { LocaleKey } from "@/i18n";
import { getFieldCopy } from "@/i18n";
import { getCdcBusinessTypeOptions } from "@/i18n/cdc-options";
import type { CdcFieldCopy } from "./cdc-field-helpers";
import countryStates from "@/data/countries-states.json" with { type: "json" };

const COUNTRY_STATE_KEYS = Object.keys(countryStates) as ReadonlyArray<string>;

type BusinessSearchCopy = {
  label: string;
  addNewAccount: string;
  canNotFindBusiness: string;
  guideline: string;
  guidelineForNameSearch: string;
  placeholder: string;
  noResults: string;
  searching?: string;
  selectedBusinessHeading: string;
  selectedBusinessNotCorrect: string;
  selectedBusinessTryAgain: string;
  selectedBusinessDismiss: string;
  resultsFound?: {
    singular?: string;
    plural?: string;
  };
  errors?: {
    network?: string;
  };
};

export type CdcBusinessSearchFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = BusinessSearchFieldProps<TFieldValues, TName> & {
  locale: LocaleKey;
};

function stripTrailingColon(value?: string) {
  if (!value) return value;
  return value.replace(/[:：]\s*$/, "");
}

function formatCountMessage(template: string, count: number) {
  return template.replace("{count}", count.toString());
}

export function CdcBusinessSearchField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  locale,
  country: countryProp = "US",
  searchMode: searchModeProp,
  businessTypeOptions: businessTypeOptionsProp,
  stateOptions: stateOptionsProp,
  label: labelProp,
  description: descriptionProp,
  manualEntryCheckboxLabel: manualEntryCheckboxLabelProp,
  searchLabel: searchLabelProp,
  searchPlaceholder: searchPlaceholderProp,
  businessTypeLabel: businessTypeLabelProp,
  businessTypePlaceholder: businessTypePlaceholderProp,
  businessNameLabel: businessNameLabelProp,
  businessNamePlaceholder: businessNamePlaceholderProp,
  addressLine1Label: addressLine1LabelProp,
  addressLine1Placeholder: addressLine1PlaceholderProp,
  addressLine2Label: addressLine2LabelProp,
  addressLine2Placeholder: addressLine2PlaceholderProp,
  cityLabel: cityLabelProp,
  cityPlaceholder: cityPlaceholderProp,
  stateLabel: stateLabelProp,
  statePlaceholder: statePlaceholderProp,
  postalCodeLabel: postalCodeLabelProp,
  postalCodePlaceholder: postalCodePlaceholderProp,
  noResultsMessage: noResultsMessageProp,
  errorFallbackMessage: errorFallbackMessageProp,
  selectedBusinessHeading: selectedBusinessHeadingProp,
  selectedBusinessDismissLabel: selectedBusinessDismissLabelProp,
  selectedBusinessNotCorrectLabel: selectedBusinessNotCorrectLabelProp,
  selectedBusinessTryAgainLabel: selectedBusinessTryAgainLabelProp,
  srMessages: srMessagesProp,
  requiredLabel: requiredLabelProp,
  ...rest
}: Readonly<CdcBusinessSearchFieldProps<TFieldValues, TName>>) {
  const businessSearchCopy = getFieldCopy(
    locale,
    "businessSearch",
  ) as unknown as BusinessSearchCopy;
  const businessTypeCopy = getFieldCopy(locale, "businessType") as CdcFieldCopy;
  const businessNameCopy = getFieldCopy(locale, "businessName") as CdcFieldCopy;
  const addressLine1Copy = getFieldCopy(locale, "addressLine1") as CdcFieldCopy;
  const addressLine2Copy = getFieldCopy(locale, "addressLine2") as CdcFieldCopy;
  const cityCopy = getFieldCopy(locale, "city") as CdcFieldCopy;
  const stateCopy = getFieldCopy(locale, "state") as CdcFieldCopy;
  const postalCodeCopy = getFieldCopy(locale, "zipCode") as CdcFieldCopy;

  const searchMode = searchModeProp ?? "postal-code";

  const fieldLabel =
    labelProp ??
    stripTrailingColon(businessSearchCopy.label) ??
    businessSearchCopy.label;
  const searchFieldLabel =
    searchLabelProp ??
    stripTrailingColon(businessSearchCopy.label) ??
    businessSearchCopy.label;

  const description =
    descriptionProp ??
    (searchMode === "business-name"
      ? businessSearchCopy.guidelineForNameSearch
      : businessSearchCopy.guideline);

  const manualEntryCheckboxLabel =
    manualEntryCheckboxLabelProp ??
    `${businessSearchCopy.canNotFindBusiness} ${businessSearchCopy.addNewAccount}`
      .replace(/\s+/g, " ")
      .trim();

  const businessTypeOptions =
    businessTypeOptionsProp ??
    getCdcBusinessTypeOptions(locale).map((option) => ({
      label: option.label,
      value: option.value,
    }));

  const stateOptions =
    stateOptionsProp && stateOptionsProp.length > 0
      ? stateOptionsProp
      : !stateOptionsProp && COUNTRY_STATE_KEYS.includes(countryProp)
        ? countryStates[countryProp as keyof typeof countryStates]
        : undefined;

  const businessTypeLabel = businessTypeLabelProp ?? businessTypeCopy.label;
  const businessTypePlaceholder =
    businessTypePlaceholderProp ?? businessTypeCopy.placeholder;

  const businessNameLabel = businessNameLabelProp ?? businessNameCopy.label;
  const businessNamePlaceholder =
    businessNamePlaceholderProp ?? businessNameCopy.placeholder;

  const addressLine1Label = addressLine1LabelProp ?? addressLine1Copy.label;
  const addressLine1Placeholder =
    addressLine1PlaceholderProp ?? addressLine1Copy.placeholder;

  const addressLine2Label = addressLine2LabelProp ?? addressLine2Copy.label;
  const addressLine2Placeholder =
    addressLine2PlaceholderProp ?? addressLine2Copy.placeholder;

  const cityLabel = cityLabelProp ?? cityCopy.label;
  const cityPlaceholder = cityPlaceholderProp ?? cityCopy.placeholder;

  const stateLabel = stateLabelProp ?? stateCopy.label;
  const statePlaceholder = statePlaceholderProp ?? stateCopy.placeholder;

  const postalCodeLabel = postalCodeLabelProp ?? postalCodeCopy.label;
  const postalCodePlaceholder =
    postalCodePlaceholderProp ?? postalCodeCopy.placeholder;

  const errorFallbackMessage =
    errorFallbackMessageProp ??
    businessSearchCopy.errors?.network ??
    "Unable to search businesses. Please try again later or enter your business manually using the checkbox below.";
  const noResultsMessage =
    noResultsMessageProp ??
    businessSearchCopy.noResults ??
    "No businesses found. Try entering your business manually using the checkbox below.";

  const selectedBusinessHeading =
    selectedBusinessHeadingProp ?? businessSearchCopy.selectedBusinessHeading;
  const selectedBusinessDismissLabel =
    selectedBusinessDismissLabelProp ??
    businessSearchCopy.selectedBusinessDismiss;
  const selectedBusinessNotCorrectLabel =
    selectedBusinessNotCorrectLabelProp ??
    businessSearchCopy.selectedBusinessNotCorrect;
  const selectedBusinessTryAgainLabel =
    selectedBusinessTryAgainLabelProp ??
    businessSearchCopy.selectedBusinessTryAgain;

  const searchPlaceholder =
    searchPlaceholderProp ?? businessSearchCopy.placeholder;

  const srMessages = srMessagesProp ?? {
    searching: businessSearchCopy.searching ?? "Searching for businesses...",
    noResults: businessSearchCopy.noResults,
    error: errorFallbackMessage,
    resultsFound: (count: number) => {
      if (count === 1) {
        const singular =
          businessSearchCopy.resultsFound?.singular ?? "1 business found";
        return formatCountMessage(singular, count);
      }

      const plural =
        businessSearchCopy.resultsFound?.plural ?? "{count} businesses found";
      return formatCountMessage(plural, count);
    },
  };

  const requiredLabel =
    requiredLabelProp ?? businessNameCopy.errors?.required ?? "Required";

  return (
    <BusinessSearchField
      {...rest}
      country={countryProp}
      searchMode={searchMode}
      label={fieldLabel}
      description={description}
      manualEntryCheckboxLabel={manualEntryCheckboxLabel}
      searchLabel={searchFieldLabel}
      searchPlaceholder={searchPlaceholder}
      businessTypeOptions={businessTypeOptions}
      businessTypeLabel={businessTypeLabel}
      businessTypePlaceholder={businessTypePlaceholder}
      businessNameLabel={businessNameLabel}
      businessNamePlaceholder={businessNamePlaceholder}
      addressLine1Label={addressLine1Label}
      addressLine1Placeholder={addressLine1Placeholder}
      addressLine2Label={addressLine2Label}
      addressLine2Placeholder={addressLine2Placeholder}
      cityLabel={cityLabel}
      cityPlaceholder={cityPlaceholder}
      stateLabel={stateLabel}
      statePlaceholder={statePlaceholder}
      stateOptions={stateOptions}
      postalCodeLabel={postalCodeLabel}
      postalCodePlaceholder={postalCodePlaceholder}
      noResultsMessage={noResultsMessage}
      errorFallbackMessage={errorFallbackMessage}
      selectedBusinessHeading={selectedBusinessHeading}
      showSelectedBusinessType={false}
      selectedBusinessDismissLabel={selectedBusinessDismissLabel}
      selectedBusinessNotCorrectLabel={selectedBusinessNotCorrectLabel}
      selectedBusinessTryAgainLabel={selectedBusinessTryAgainLabel}
      srMessages={srMessages}
      requiredLabel={requiredLabel}
    />
  );
}
